import React, { useState, useEffect, useRef } from 'react';
import { migration } from '../lib/api';
import toast from 'react-hot-toast';
import { Database, Play, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle size={16} className="text-green-500" />;
  if (status === 'failed') return <XCircle size={16} className="text-red-500" />;
  if (status === 'running') return <RefreshCw size={16} className="text-blue-500 animate-spin" />;
  return <Clock size={16} className="text-gray-400" />;
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{value} / {max} files</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function MigrationPage() {
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [polling, setPolling] = useState(null);
  const [starting, setStarting] = useState(false);
  const [recordCount, setRecordCount] = useState(25);
  const [jobName, setJobName] = useState('Alchemy Production Migration');
  const logRef = useRef(null);

  const loadJobs = async () => {
    try {
      const { data } = await migration.jobs();
      setJobs(data);
    } catch (e) {}
  };

  useEffect(() => {
    loadJobs();
    return () => { if (polling) clearInterval(polling); };
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [activeJob?.log]);

  const startPolling = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await migration.getJob(jobId);
        setActiveJob(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
          setPolling(null);
          loadJobs();
          if (data.status === 'completed') toast.success(`Migration complete: ${data.processed_files} records imported`);
        }
      } catch (e) { clearInterval(interval); }
    }, 800);
    setPolling(interval);
    return interval;
  };

  const handleSimulate = async () => {
    setStarting(true);
    try {
      const { data } = await migration.simulate({ job_name: jobName, record_count: recordCount });
      toast.success('Migration simulation started');
      const jobData = { id: data.job_id, name: jobName, status: 'running', total_files: recordCount, processed_files: 0, failed_files: 0, log: [] };
      setActiveJob(jobData);
      startPolling(data.job_id);
      loadJobs();
    } catch (e) {
      toast.error('Failed to start migration');
    } finally {
      setStarting(false);
    }
  };

  const ALCHEMY_FORMATS = ['.tif','.tiff','.ocr','.dat','.pro','.alb','.fdr','.ndx','.rid','.rob','.pdf','.doc','.docx'];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Alchemy migration</h1>
        <p className="text-sm text-gray-500 mt-1">Import legacy records from OpenText Alchemy into CogniVault</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Simulator */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Database size={16} className="text-blue-500" />
            <h2 className="text-sm font-medium text-gray-900">Migration simulator</h2>
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full ml-auto">Demo mode</span>
          </div>

          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Job name</label>
              <input value={jobName} onChange={e => setJobName(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Simulated record count: <span className="text-blue-600 font-semibold">{recordCount.toLocaleString()}</span>
              </label>
              <input type="range" min="10" max="200" step="5" value={recordCount} onChange={e => setRecordCount(+e.target.value)}
                className="w-full accent-blue-500" />
              <div className="flex justify-between text-xs text-gray-300 mt-1"><span>10</span><span>200</span></div>
            </div>
          </div>

          {/* Supported formats */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="text-xs font-medium text-gray-500 mb-2">Supported Alchemy formats</div>
            <div className="flex flex-wrap gap-1.5">
              {ALCHEMY_FORMATS.map(f => (
                <span key={f} className="text-xs bg-white text-gray-500 px-2 py-0.5 rounded border border-gray-200 font-mono">{f}</span>
              ))}
            </div>
          </div>

          <button onClick={handleSimulate} disabled={starting || !!polling}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            <Play size={14} />
            {starting ? 'Starting...' : polling ? 'Migration running...' : 'Run simulation'}
          </button>
        </div>

        {/* Live log */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-medium text-gray-900">Live migration log</h2>
            {activeJob && <StatusIcon status={activeJob.status} />}
          </div>

          {!activeJob ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-300 bg-gray-50 rounded-lg">
              No active migration. Run a simulation to see the log.
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="text-sm font-medium text-gray-700">{activeJob.name}</div>
                {activeJob.total_files > 0 && <ProgressBar value={activeJob.processed_files} max={activeJob.total_files} />}
                {activeJob.failed_files > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600">
                    <AlertCircle size={12} /> {activeJob.failed_files} file{activeJob.failed_files !== 1 ? 's' : ''} failed
                  </div>
                )}
              </div>
              <div ref={logRef} className="flex-1 bg-gray-900 rounded-lg p-3 overflow-y-auto font-mono text-xs space-y-0.5 min-h-[200px] max-h-[280px]">
                {(activeJob.log || []).map((entry, i) => (
                  <div key={i} className={`flex items-start gap-2 ${entry.status === 'failed' || entry.status === 'error' ? 'text-red-400' : entry.status === 'success' ? 'text-green-400' : 'text-gray-400'}`}>
                    <span className="text-gray-600 flex-shrink-0">{entry.ts ? format(new Date(entry.ts), 'HH:mm:ss') : '--:--:--'}</span>
                    <span>[{entry.status?.toUpperCase()}]</span>
                    <span className="truncate">{entry.title || entry.file}{entry.error ? ` — ${entry.error}` : ''}{entry.format ? ` (${entry.format})` : ''}</span>
                  </div>
                ))}
                {activeJob.status === 'running' && (
                  <div className="text-blue-400 flex items-center gap-2">
                    <span className="animate-pulse">▊</span> Processing...
                  </div>
                )}
                {activeJob.status === 'completed' && (
                  <div className="text-teal-400 mt-2">✓ Migration complete — {activeJob.processed_files} records imported to CogniVault</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Past jobs */}
      {jobs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="text-sm font-medium text-gray-700">Migration history</h2>
          </div>
          <table className="w-full">
            <thead><tr className="border-b border-gray-50 bg-gray-50/50">
              {['Job name','Status','Records','Failed','Started','Duration'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {jobs.map(j => {
                const duration = j.started_at && j.completed_at
                  ? Math.round((new Date(j.completed_at) - new Date(j.started_at)) / 1000) + 's'
                  : j.status === 'running' ? 'Running...' : '—';
                return (
                  <tr key={j.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors cursor-pointer"
                    onClick={() => setActiveJob(j)}>
                    <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{j.name}</td>
                    <td className="px-5 py-3.5"><div className="flex items-center gap-1.5"><StatusIcon status={j.status} /><span className="text-sm text-gray-600 capitalize">{j.status}</span></div></td>
                    <td className="px-5 py-3.5 text-sm text-gray-600">{j.processed_files?.toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-600">{j.failed_files > 0 ? <span className="text-amber-600">{j.failed_files}</span> : '0'}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">{j.started_at ? format(new Date(j.started_at), 'MMM d, HH:mm') : '—'}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">{duration}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
